import * as React from 'react';

import { priceFormatter } from '@blockworks/platform';
import type { GetAssetResponse, GetGlobalHistoryResponse } from '@blockworks/platform/api/research';
import { AssetTab, PostType } from '@blockworks/platform/api/strapi';
import { CompactNumber } from '@blockworks/ui';

import { PriceChangePlain } from '@/components/common';
import UpcomingEvents from '@/components/data-fetchers/events/upcoming-events';
import Newsletter from '@/components/pages/frontpage/components/feature/right/newsletter';
import PriceAboutDetail from '@/components/pages/price/components/content/about/price-about-detail';
import PriceAboutSummary from '@/components/pages/price/components/content/about/price-about-summary';
import FaqItem from '@/components/pages/price/components/content/faq/faq-item';
import PriceChartWithInfo from '@/components/pages/price/components/content/price-chart-with-info';
import PriceBreadcrumb from '@/components/pages/price/components/header/price-breadcrumb';
import PriceHead from '@/components/pages/price/components/header/price-head';
import PriceSectionHeader from '@/components/pages/price/components/header/price-section-header';
import PriceMarketData from '@/components/pages/price/components/market-data/price-market-data';
import TopPriceInfoHeader from '@/components/pages/price/components/top-price-info-header';
import NewsRecirculationSection from '@/components/sections/content/news-recirculation-section';
import RecentResearchFetchable from '@/components/sections/right/recent-research-fetchable';
import RecentResearchHeader from '@/components/sections/right/recent-research-header';
import { ArticleType } from '@/types/article-type';
import subscribeToNewsletter from '@/utils/functions/subscribe-to-newsletter';
import { Category } from '@/utils/functions/wordpress/fetch-categories';
import useAnalyticsReadTracker from '@/utils/hooks/use-analytics-read-tracker';

type PricePageProps = {
    asset: GetAssetResponse['data'];
    recentArticles: PostType[] | null;
    globalMarketHistory: GetGlobalHistoryResponse['data'];
    readMoreArticles: ArticleType[];
    categories: Category[];
    sponsorName?: string;
    summary: AssetTab | null;
    about: AssetTab | null;
};

const PricePageLayout = (props: PricePageProps) => {
    const { asset, summary, about, recentArticles } = props;
    // Analytics
    const newsSectionRef = useAnalyticsReadTracker(
        'Price.Read',
        {
            title: asset.title,
            slug: asset.slug,
            code: asset.code,
            categories: [asset.category],
        },
        asset.code,
    );

    return (
        <main
            className="border-gray-200 w-full"
            itemScope
            itemType="https://schema.org/SomeProducts"
            itemID={asset.code.toLowerCase()}
        >
            <div className="p-6 lg:p-10">
                <div className="flex flex-col justify-start items-start flex-grow relative gap-2">
                    <PriceBreadcrumb title={asset.title} code={asset.code} />
                    <div className="flex justify-start items-center relative gap-2 py-2">
                        <TopPriceInfoHeader
                            className="text-4xl md:text-6xl"
                            title={asset.title}
                            code={asset.code}
                            imageUrl={asset.imageUrl}
                        />
                    </div>
                    <p className="text-lg lg:text-xl text-left text-gray-500">
                        Live {asset.title} price updates and the latest {asset.title} news.
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap">
                <div className="item w-full lg:w-8/12 lg:border-r">
                    <PriceHead
                        sponsoredBy={
                            <a href="https://www.blockworksresearch.com" rel="nofollow noreferrer" target="_blank">
                                Blockworks Research
                            </a>
                        }
                    />
                    <PriceChartWithInfo asset={asset} />
                    <main>
                        <PriceAboutSummary
                            title={asset.title}
                            description={asset.description as string}
                            summary={summary}
                        />
                        <section>
                            <PriceAboutDetail code={asset.code} hidePageTitle about={about} />
                            <PriceSectionHeader title={`${asset.title} Stats`} />
                            <div className="grid grid-cols-1 lg:grid-cols-2 p-8 gap-8">
                                <FaqItem question={`What is the market cap of ${asset.title}?`}>
                                    The current market cap of {asset.title} is $
                                    <CompactNumber className="font-semibold" number={asset.marketcap.usd} />. A high
                                    market capitalization implies that the asset is highly valued by the market.
                                </FaqItem>
                                <FaqItem question={`What is the current trading activity of ${asset.title}?`}>
                                    Currently,{' '}
                                    <CompactNumber
                                        className="font-semibold"
                                        number={asset.marketcap.usdVolume24h / asset.price.usd}
                                    />{' '}
                                    of {asset.code} were traded within 24 hours of time. In other words, $
                                    <CompactNumber className="font-semibold" number={asset.marketcap.usdVolume24h} />{' '}
                                    have changed hands within the past 24 hours through trading. That&apos;s a change of{' '}
                                    <PriceChangePlain
                                        className="inline-flex"
                                        priceChangePercent={asset.change.usd24h}
                                        useColorCode
                                    />
                                    .
                                </FaqItem>
                                <FaqItem question={`What is the current price of ${asset.title}?`}>
                                    The price of 1 {asset.title} currently costs{' '}
                                    <span className="font-semibold">{priceFormatter(asset?.price?.usd || 0)}</span>.
                                </FaqItem>
                                <FaqItem question={`How many ${asset.title} are there?`}>
                                    The current circulating supply of {asset.title} is{' '}
                                    <CompactNumber number={asset.supply.circulating} />. This is the total amount of{' '}
                                    {asset.code} that is available.
                                </FaqItem>
                                <FaqItem question={`What is the relative popularity of ${asset.title}?`}>
                                    {asset.title} ({asset.code}) currently ranks{' '}
                                    <span className="font-semibold">{asset.marketcap.rank}</span> among all known
                                    cryptocurrency assets. The popularity is based on relative market cap.
                                </FaqItem>
                            </div>
                        </section>
                    </main>
                </div>
                <div className="item w-full lg:w-4/12 lg:pb-8">
                    <PriceMarketData
                        usd24hChange={asset.change.usd24h}
                        usd24hVolume={asset.marketcap.usdVolume24h}
                        usdMarketCap={asset.marketcap.usd}
                        circulatingSupply={asset.supply.circulating}
                        marketCapRank={asset.marketcap.rank}
                    />
                    <div className="hidden lg:block">
                        <Newsletter
                            title="Newsletter"
                            subTitle="The Breakdown"
                            description="Decoding crypto and the markets. Daily, with Byron Gilliam."
                            inputPlaceholder="Email address"
                            buttonText="subscribe"
                            onButtonClick={subscribeToNewsletter}
                            className="border-t md:border-0 lg:border-t md:border-l lg:border-l-0 md:w-1/2 lg:w-full"
                        />
                        <UpcomingEvents />
                        <RecentResearchHeader title={'recent research'} />
                        <RecentResearchFetchable recentArticles={recentArticles} hideHeader />
                    </div>
                </div>
            </div>
            <NewsRecirculationSection
                title={`latest ${asset.title} news`}
                algoliaSearchKeyword={asset.title}
                algoliaRef={newsSectionRef}
                linkTitle={
                    <span className="flex align-middle">
                        more from news
                        <svg
                            width={8}
                            height={4}
                            viewBox="0 0 8 4"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="ml-1 mt-1.5"
                            preserveAspectRatio="none"
                        >
                            <path d="M6.005 1.5H0V2.5H6.005V4L8 2L6.005 0V1.5Z" fill="#5637CD" />
                        </svg>
                    </span>
                }
                linkUrl="/news"
            />
        </main>
    );
};

export default PricePageLayout;
